import React, { ReactElement, useEffect, useState } from "react";
import {
    LegacyBucketFile,
    ConfirmDialog,
    createListTableColumns,
    ensure,
    Icon,
    ShimmeredDetailsList,
    Spinner,
    SpinnerSize,
    Stack,
    useNotifications
} from "sprint-common-web";

interface ProcessBucketFilesProps {
    selectedFiles: LegacyBucketFile[];
    title: string;
    message?: string;
    autoConfirm?: boolean;
    onProcessFile(file: LegacyBucketFile): Promise<void>;
    onProcessingComplete(successfullyProcessedFiles: LegacyBucketFile[]): void;
}

type ProcessBucketFileItem = LegacyBucketFile & {
    status: ActionStatus;
};

enum ActionStatus {
    WaitingForConfirmation,
    InProgress,
    Completed,
    Failed
}

export const ProcessBucketFilesDialog: React.VFC<ProcessBucketFilesProps> = ({
    selectedFiles,
    title,
    message,
    autoConfirm,
    onProcessFile,
    onProcessingComplete
}: ProcessBucketFilesProps) => {
    const { success } = useNotifications();

    const [isActionInProgress, setIsActionInProgress] = useState<boolean>(false);
    const [items, setItems] = useState<ProcessBucketFileItem[]>(
        selectedFiles.map((item) => ({ ...item, status: ActionStatus.WaitingForConfirmation }))
    );

    const isFilesProcessingFinished = items.every(isProcessingFinished);

    useEffect(() => {
        if (items.length && autoConfirm && !isActionInProgress) {
            process();
        }
    }, [items, items.length, autoConfirm]);

    const setStatusByFilePath = (filePath: string, status: ActionStatus) => {
        setItems((prevItems) =>
            prevItems.map((prevItem) => (prevItem.path === filePath ? { ...prevItem, status } : prevItem))
        );
    };

    const handleProcessingComplete = () => {
        const processingCompletedItems = items.filter(({ status }) => status === ActionStatus.Completed);

        onProcessingComplete(processingCompletedItems.map(({ status, ...item }) => item));
    };

    const process = async () => {
        const results = await processAllItems();

        if (results.every(({ status }) => status === "fulfilled")) {
            onProcessingComplete(items.map(({ status, ...item }) => item));

            success("Processing complete");
        }
    };

    const processAllItems = async () => {
        setIsActionInProgress(true);

        const results = await Promise.allSettled(
            items.map(async (item) => {
                setStatusByFilePath(item.path, ActionStatus.InProgress);

                try {
                    await onProcessFile(item);

                    setStatusByFilePath(item.path, ActionStatus.Completed);
                } catch (error) {
                    setStatusByFilePath(item.path, ActionStatus.Failed);

                    throw error;
                }
            })
        );

        setIsActionInProgress(false);

        return results;
    };

    return (
        <ConfirmDialog
            title={title}
            text={message || "Are you sure you want to start processing listed files?"}
            isBlocking={isActionInProgress}
            showCloseButton={false}
            confirm={{
                onClick: process,
                disabled: autoConfirm ? true : false
            }}
            cancel={{
                onClick: handleProcessingComplete,
                disabled: isActionInProgress || isFilesProcessingFinished
            }}
            width={700}
            contentHeight={400}
        >
            <ItemsSummary items={items} />
        </ConfirmDialog>
    );
};

interface ItemsSummaryProps {
    items: ProcessBucketFileItem[];
}

const ItemsSummary: React.VFC<ItemsSummaryProps> = ({ items }: ItemsSummaryProps) => {
    const columns = createListTableColumns<ProcessBucketFileItem>([
        {
            type: "basic",
            key: "path",
            name: "File path",
            minWidth: 200
        },
        {
            type: "basic",
            key: "status",
            name: "Status",
            minWidth: 40,
            onRender: renderActionStatus
        }
    ]);

    return <ShimmeredDetailsList columns={columns} items={items} isHeaderVisible={false} layoutMode="justified" />;
};

export const renderActionStatus = (item?: ProcessBucketFileItem): ReactElement | null => {
    ensure(item);

    const status = item["status"];

    const indicator = (() => {
        switch (status) {
            case ActionStatus.WaitingForConfirmation: {
                return <></>;
            }

            case ActionStatus.InProgress: {
                return <Spinner size={SpinnerSize.small} />;
            }

            case ActionStatus.Completed: {
                return <Icon iconName="Accept" />;
            }

            case ActionStatus.Failed: {
                return <Icon iconName="Cancel" />;
            }
        }
    })();

    return <Stack horizontalAlign="start">{indicator}</Stack>;
};

const isProcessingFinished = (items: ProcessBucketFileItem) =>
    items.status === ActionStatus.Completed || items.status === ActionStatus.Failed;
